"use client";

import { Button } from "@package/subframe/components/Button";
import { IconButton } from "@package/subframe/components/IconButton";
import { TextField } from "@package/subframe/components/TextField";
import { FeatherX } from "@subframe/core";
import { useQueryClient } from "@tanstack/react-query";
import { isAxiosError } from "axios";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { z } from "zod";

import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Spinner } from "@/components/ui/spinner";
import { useNursery } from "@/contexts/NurseryContext";
import {
  useCreateNurseryCommandControllerCreate,
  useGetNurseryQueryControllerGet,
  useUpdateNurseryCommandControllerUpdate,
} from "@/generated/nurseries/nurseries";

const branchSchema = z.object({
  name: z
    .string()
    .trim()
    .min(1, "Nursery name is required")
    .max(100, "Name must be less than 100 characters"),
  street: z
    .string()
    .trim()
    .min(1, "Street address is required")
    .max(200, "Street must be less than 200 characters"),
  city: z
    .string()
    .trim()
    .min(1, "City is required")
    .max(100, "City must be less than 100 characters"),
  postcode: z
    .string()
    .trim()
    .min(1, "Postcode is required")
    .max(20, "Postcode must be less than 20 characters"),
  country: z
    .string()
    .trim()
    .max(100, "Country must be less than 100 characters")
    .optional(),
});

interface BranchFormDialogProps {
  organisationId: string;
  branchId?: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function BranchFormDialog({
  organisationId,
  branchId,
  open,
  onOpenChange,
}: BranchFormDialogProps) {
  const queryClient = useQueryClient();
  const { refreshNurseries, setSelectedNursery } = useNursery();
  const mode = branchId ? "edit" : "create";

  const [name, setName] = useState("");
  const [street, setStreet] = useState("");
  const [city, setCity] = useState("");
  const [postcode, setPostcode] = useState("");
  const [country, setCountry] = useState("UK");
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Fetch branch data if in edit mode
  const { data: branchData, isLoading: isLoadingBranch } =
    useGetNurseryQueryControllerGet(branchId || "", {
      query: {
        enabled: !!branchId && open,
      },
    });

  // Populate form when branch data loads or dialog opens
  useEffect(() => {
    if (branchData?.data && mode === "edit" && open) {
      const branch = branchData.data;
      setName(branch.name || "");
      setStreet(branch.address?.street || "");
      setCity(branch.address?.city || "");
      setPostcode(branch.address?.postcode || "");
      setCountry(branch.address?.country || "UK");
    }
  }, [branchData, mode, open]);

  // Reset form when modal closes or switches to create mode
  useEffect(() => {
    if (!open || mode === "create") {
      setName("");
      setStreet("");
      setCity("");
      setPostcode("");
      setCountry("UK");
      setErrors({});
    }
  }, [open, mode]);

  const { mutate: createBranch, isPending: isCreating } =
    useCreateNurseryCommandControllerCreate({
      mutation: {
        onSuccess: async (response) => {
          toast.success("Nursery created successfully");
          await queryClient.invalidateQueries({
            predicate: (query) =>
              Array.isArray(query.queryKey) &&
              typeof query.queryKey[0] === "string" &&
              query.queryKey[0].includes("/nurseries"),
            refetchType: "all",
          });
          await refreshNurseries();

          // Auto-select the newly created nursery
          const newNurseryId = response?.data?.id;
          if (newNurseryId) {
            setSelectedNursery(newNurseryId, organisationId);
          }

          onOpenChange(false);
        },
        onError: (error: unknown) => {
          const errorMessage =
            isAxiosError(error) && error.response?.data?.message
              ? error.response.data.message
              : "Failed to create nursery";
          toast.error(errorMessage);
        },
      },
    });

  const { mutate: updateBranch, isPending: isUpdating } =
    useUpdateNurseryCommandControllerUpdate({
      mutation: {
        onSuccess: async () => {
          toast.success("Nursery updated successfully");
          await queryClient.invalidateQueries({
            predicate: (query) =>
              Array.isArray(query.queryKey) &&
              typeof query.queryKey[0] === "string" &&
              query.queryKey[0].includes("/nurseries"),
            refetchType: "all",
          });
          onOpenChange(false);
        },
        onError: (error: unknown) => {
          const errorMessage =
            error instanceof Error ? error.message : "Failed to update nursery";
          toast.error(errorMessage);
        },
      },
    });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const result = branchSchema.safeParse({
      name,
      street,
      city,
      postcode,
      country: country || undefined,
    });

    if (!result.success) {
      const fieldErrors: Record<string, string> = {};
      result.error.issues.forEach((err) => {
        const path = err.path.join(".");
        fieldErrors[path] = err.message;
      });
      setErrors(fieldErrors);
      return;
    }

    setErrors({});

    const addressData = {
      street,
      city,
      postcode,
      country: country || "UK",
    };

    if (mode === "create") {
      createBranch({
        data: {
          name,
          organisationId,
          address: addressData,
        },
      });
    } else if (branchId) {
      updateBranch({
        id: branchId,
        data: {
          name,
          address: addressData,
        },
      });
    }
  };

  const isPending = isCreating || isUpdating;
  const isLoading = isLoadingBranch && mode === "edit";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        showCloseButton={false}
        className="max-w-2xl max-h-[90vh] overflow-y-auto p-0"
      >
        <DialogTitle className="sr-only">
          {mode === "edit" ? "Edit Nursery" : "Add Nursery"}
        </DialogTitle>

        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Spinner className="h-8 w-8 text-primary" />
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="flex flex-col">
            {/* Header */}
            <div className="flex items-center justify-between border-b border-solid border-secondary-beige-200 px-6 py-4">
              <div className="flex flex-col items-start gap-1">
                <span className="text-heading-2 font-heading-2 text-default-font">
                  {mode === "edit" ? "Edit Nursery" : "Add Nursery"}
                </span>
                <span className="text-body font-body text-subtext-color">
                  {mode === "edit"
                    ? "Update nursery details."
                    : "Create a new nursery for this nursery group."}
                </span>
              </div>
              <IconButton
                variant="neutral-tertiary"
                size="medium"
                icon={<FeatherX />}
                onClick={() => onOpenChange(false)}
              />
            </div>

            {/* Form Content */}
            <div className="flex flex-col gap-6 px-6 py-6">
              <TextField
                className="h-auto w-full"
                size="large"
                label="Nursery name"
                error={!!errors.name}
                helpText={
                  errors.name || "A unique name to identify this nursery"
                }
              >
                <TextField.Input
                  placeholder="e.g., Main Street Nursery"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  disabled={isPending}
                />
              </TextField>

              <TextField
                className="h-auto w-full"
                size="large"
                label="Street address"
                error={!!errors.street}
                helpText={errors.street}
              >
                <TextField.Input
                  placeholder="e.g., 123 Main Street"
                  value={street}
                  onChange={(e) => setStreet(e.target.value)}
                  disabled={isPending}
                />
              </TextField>

              <div className="flex w-full items-start gap-4">
                <TextField
                  className="h-auto grow shrink-0 basis-0"
                  size="large"
                  label="City"
                  error={!!errors.city}
                  helpText={errors.city}
                >
                  <TextField.Input
                    placeholder="e.g., London"
                    value={city}
                    onChange={(e) => setCity(e.target.value)}
                    disabled={isPending}
                  />
                </TextField>

                <TextField
                  className="h-auto grow shrink-0 basis-0"
                  size="large"
                  label="Postcode"
                  error={!!errors.postcode}
                  helpText={errors.postcode}
                >
                  <TextField.Input
                    placeholder="e.g., SW1A 1AA"
                    value={postcode}
                    onChange={(e) => setPostcode(e.target.value)}
                    disabled={isPending}
                  />
                </TextField>
              </div>

              <TextField
                className="h-auto w-full"
                size="large"
                label="Country"
                error={!!errors.country}
                helpText={errors.country}
              >
                <TextField.Input
                  placeholder="e.g., UK"
                  value={country}
                  onChange={(e) => setCountry(e.target.value)}
                  disabled={isPending}
                />
              </TextField>
            </div>

            {/* Footer */}
            <div className="flex items-center justify-end gap-3 border-t border-solid border-secondary-beige-200 px-6 py-4">
              <Button
                type="button"
                variant="neutral-primary"
                size="large"
                onClick={() => onOpenChange(false)}
                disabled={isPending}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="brand-primary"
                size="large"
                disabled={isPending}
              >
                {isPending
                  ? mode === "edit"
                    ? "Saving..."
                    : "Creating..."
                  : mode === "edit"
                    ? "Save Changes"
                    : "Create Nursery"}
              </Button>
            </div>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
